/* eslint-disable jsx-a11y/interactive-supports-focus */

'use client';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { Star, StarHalf } from 'phosphor-icons';
import {
  Children,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  cloneElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { roundToNearestHalfStar } from 'utils';
import { c } from '../../../utils/tailwind-merge';
import {
  RatingButtonProps,
  RatingContextValue,
  RatingProps,
} from './Rating.types';

/* Utility hook and context for Rating component https://www.shadcn.io/button/rating */

const RatingContext = createContext<RatingContextValue | null>(null);

const useRating = () => {
  const context = useContext(RatingContext);
  if (!context) {
    throw new Error('useRating must be used within a Rating component');
  }
  return context;
};

export const RatingButton = ({
  index: providedIndex,
  size = 20,
  className,
  icon = <Star />,
  width = 16,
  height = 16,
  variant = 'black',
  microcopy,
  editable = false,
}: RatingButtonProps) => {
  const {
    value,
    readOnly,
    hoverValue,
    focusedStar,
    handleValueChange,
    handleKeyDown,
    setHoverValue,
    setFocusedStar,
  } = useRating();

  const index = providedIndex ?? 0;

  let displayValue = hoverValue ?? focusedStar ?? value ?? 0;

  if (readOnly) {
    displayValue = roundToNearestHalfStar(displayValue);
  }

  const fullStars = Math.floor(displayValue);
  const hasHalfStar = displayValue - fullStars === 0.5;

  const isFull = index + 1 <= fullStars;
  const isHalf = !isFull && hasHalfStar && index === fullStars;
  const isActive = isFull || isHalf;

  const getTabbableIndex = (): number => {
    if (readOnly) return -1;
    if (focusedStar !== null) return focusedStar - 1;
    if (value && value > 0) return value - 1;
    return 0;
  };

  const tabIndex = getTabbableIndex() === index ? 0 : -1;

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      handleValueChange(event, index + 1);
    },
    [handleValueChange, index]
  );

  const handleMouseEnter = useCallback(() => {
    if (!readOnly) {
      setHoverValue(index + 1);
    }
  }, [readOnly, setHoverValue, index]);

  const handleFocus = useCallback(() => {
    setFocusedStar(index + 1);
  }, [setFocusedStar, index]);

  const handleBlur = useCallback(() => {
    setFocusedStar(null);
  }, [setFocusedStar]);

  const baseIcon = isHalf ? <StarHalf /> : icon;

  const variantColor = variant === 'black' ? 'text-grey-850' : 'text-blue';

  const getStarFillColor = () => {
    if (readOnly) {
      return variantColor;
    }
    return isActive ? variantColor : 'text-grey-900';
  };

  return (
    <button
      className={c(
        'focus-visible:ring-ring rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        readOnly && 'cursor-default',
        className
      )}
      disabled={readOnly && !editable}
      onBlur={handleBlur}
      onClick={handleClick}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      tabIndex={tabIndex}
      type="button"
      {...(microcopy?.ariaLabel ? { 'aria-label': microcopy.ariaLabel } : {})}
      data-testid="star-icon-button" // CanSubmitWithRatingAndReviewTest needs a different id to work in mobile
    >
      {cloneElement(baseIcon, {
        size,
        weight: isActive ? 'fill' : 'regular',
        className: c(
          'transition-colors duration-200 text-grey-900',
          getStarFillColor(),
          !readOnly && 'cursor-pointer'
        ),
        'aria-hidden': 'true',
        width,
        height,
      })}
    </button>
  );
};

export const Rating = ({
  value: controlledValue,
  onValueChange: controlledOnValueChange,
  defaultValue = 0,
  onChange,
  readOnly = false,
  className,
  microcopy,
  children,
  ...props
}: RatingProps) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const [focusedStar, setFocusedStar] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [value, onValueChange] = useControllableState({
    defaultProp: defaultValue,
    prop: controlledValue,
    onChange: controlledOnValueChange,
  });
  const handleValueChange = useCallback(
    (
      event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>,
      newValue: number
    ) => {
      if (!readOnly) {
        onChange?.(event, newValue);
        onValueChange?.(newValue);
      }
    },
    [readOnly, onChange, onValueChange]
  );
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (readOnly) {
        return;
      }
      const total = Children.count(children);
      let newValue = focusedStar !== null ? focusedStar : value ?? 0;
      switch (event.key) {
        case 'ArrowRight':
          if (event.shiftKey || event.metaKey) {
            newValue = total;
          } else {
            newValue = Math.min(total, newValue + 1);
          }
          break;
        case 'ArrowLeft':
          if (event.shiftKey || event.metaKey) {
            newValue = 1;
          } else {
            newValue = Math.max(1, newValue - 1);
          }
          break;
        default:
          return;
      }
      event.preventDefault();
      setFocusedStar(newValue);
      handleValueChange(event, newValue);
    },
    [focusedStar, value, children, readOnly, handleValueChange]
  );
  useEffect(() => {
    if (focusedStar !== null && containerRef.current) {
      const buttons = containerRef.current.querySelectorAll('button');
      buttons[focusedStar - 1]?.focus();
    }
  }, [focusedStar]);
  const contextValue: RatingContextValue = {
    value: value ?? 0,
    readOnly,
    hoverValue,
    focusedStar,
    handleValueChange,
    handleKeyDown,
    setHoverValue,
    setFocusedStar,
  };
  return (
    <RatingContext.Provider value={contextValue}>
      <div
        aria-label={microcopy?.ariaLabel ?? 'Rating'}
        className={c('inline-flex items-center gap-0.5', className)}
        onMouseLeave={() => setHoverValue(null)}
        ref={containerRef}
        role="radiogroup"
        {...props}
      >
        {Children.map(children, (child, index) => {
          if (!child) {
            return null;
          }
          return cloneElement(child as ReactElement<RatingButtonProps>, {
            index,
          });
        })}
      </div>
    </RatingContext.Provider>
  );
};
